import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faXmark } from '@crisp/crisp-ui/icons/classic/regular';
import { Icon } from '../mdx/Icon';
import { isNavGroup } from '../../lib/navAccess';
import { NavGroup, NavPageItem } from '../../types';
import { cn } from '../../lib/cn';
import styles from './Sidebar.module.css';

interface SidebarProps {
  groups: NavGroup[];
  routePrefix?: string;
  homeSlug?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

function cleanSlug(slug?: string): string {
  if (!slug) return '';
  return (
    slug
      .replace(/^https?:\/\/[^/]+/, '')
      .split('#')[0]
      .split('?')[0]
      .replace(/^\/+/, '')
      .replace(/\/+$/, '') || 'index'
  );
}

function sectionContainsPath(item: NavGroup | NavPageItem, targetPath: string): boolean {
  const target = cleanSlug(targetPath);
  if (!target) return false;

  if (isNavGroup(item)) {
    return (item.pages || []).some((child) => sectionContainsPath(child, target));
  }

  const slug = cleanSlug(item.slug);
  return slug === target;
}

function firstPage(group: NavGroup): NavPageItem | undefined {
  for (const item of group.pages || []) {
    const page = isNavGroup(item) ? firstPage(item) : item;
    if (page) return page;
  }
  return undefined;
}

export const SIDEBAR_WIDTH_STORAGE_KEY = 'crisp_docs_sidebar_width';
const DEFAULT_WIDTH = 288;
const MIN_WIDTH = 224;
const MAX_WIDTH = 480;
const KEYBOARD_STEP = 16;

function clampWidth(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));
}

function readStoredWidth(): number {
  try {
    const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    return stored ? clampWidth(stored) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function storeWidth(width: number): void {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // Private mode / blocked storage — the width just won't persist.
  }
}

function formatSlugTitle(slug: string): string {
  if (!slug || slug === 'index') return 'Welcome to Crisp Support';
  const last = slug.split('/').pop() || slug;
  return last
    .replace(/^articles-/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export const Sidebar: React.FC<SidebarProps> = ({
  groups,
  routePrefix = '',
  homeSlug = 'index',
  isOpen = false,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const relativePath =
    routePrefix && location.pathname.startsWith(routePrefix)
      ? location.pathname.slice(routePrefix.length)
      : location.pathname;
  const currentPath = cleanSlug(relativePath);

  const [userToggled, setUserToggled] = useState<Record<string, boolean>>({});
  const [width, setWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const commitWidth = (next: number) => {
    const clamped = clampWidth(next);
    setWidth(clamped);
    storeWidth(clamped);
  };

  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startWidth: width };
    setIsResizing(true);
  };

  const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    setWidth(clampWidth(dragRef.current.startWidth + e.clientX - dragRef.current.startX));
  };

  const onResizePointerEnd = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsResizing(false);
    storeWidth(width);
  };

  const onResizeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') commitWidth(width - KEYBOARD_STEP);
    else if (e.key === 'ArrowRight') commitWidth(width + KEYBOARD_STEP);
    else if (e.key === 'Home') commitWidth(MIN_WIDTH);
    else if (e.key === 'End') commitWidth(MAX_WIDTH);
    else return;
    e.preventDefault();
  };

  useEffect(() => {
    if (!isResizing) return;
    const { style } = document.body;
    const previous = { cursor: style.cursor, userSelect: style.userSelect };
    style.cursor = 'col-resize';
    style.userSelect = 'none';
    return () => {
      style.cursor = previous.cursor;
      style.userSelect = previous.userSelect;
    };
  }, [isResizing]);

  useEffect(() => {
    setUserToggled({});
  }, [location.pathname]);

  const toggleSection = (key: string, isExpanded: boolean) => {
    setUserToggled((prev) => ({
      ...prev,
      [key]: !isExpanded,
    }));
  };

  const pageHref = (page: NavPageItem) => {
    const pageSlug = page.slug === homeSlug ? '' : page.slug;
    return `${routePrefix}${pageSlug ? `/${pageSlug}` : ''}` || '/';
  };

  const renderPageItem = (page: NavPageItem, depth = 0) => {
    const cleanItemSlug = cleanSlug(page.slug);
    const isActive =
      currentPath === cleanItemSlug ||
      ((currentPath === '' || currentPath === 'index') && page.slug === homeSlug);

    const title = page.sidebarTitle || page.title || formatSlugTitle(page.slug);
    const paddingLeft = `${12 + (depth + 1) * 12}px`;

    return (
      <li key={page.slug}>
        <Link
          to={pageHref(page)}
          onClick={onClose}
          className={cn(styles.row, isActive && styles.rowActive)}
          style={{ paddingLeft }}
        >
          <div className={styles.rowInner}>
            {page.icon && <Icon name={page.icon} />}
            <span className={styles.label}>{title}</span>
          </div>
        </Link>
      </li>
    );
  };

  const renderGroup = (group: NavGroup, depth = 0, pathKey = ''): React.ReactNode => {
    const key = pathKey ? `${pathKey}/${group.group}` : group.group;
    const isWithin = sectionContainsPath(group, currentPath || homeSlug);
    const isExpanded = userToggled[key] !== undefined ? userToggled[key] : isWithin;
    const headerPaddingLeft = `${12 + depth * 12}px`;
    const isTopLevel = depth === 0;

    return (
      <div key={key} className={isTopLevel ? styles.topGroup : undefined}>
        {group.group && (
          <button
            type="button"
            onClick={() => {
              const target = !isExpanded && !isWithin ? firstPage(group) : undefined;
              if (target) {
                navigate(pageHref(target));
                onClose?.();
              } else {
                toggleSection(key, isExpanded);
              }
            }}
            aria-expanded={isExpanded}
            className={cn(styles.row, isWithin && styles.rowWithin)}
            style={{ paddingLeft: headerPaddingLeft }}
          >
            <div className={styles.rowInner}>
              {group.icon && <Icon name={group.icon} />}
              <span className={styles.label}>{group.group}</span>
            </div>
            <FontAwesomeIcon
              icon={faChevronRight}
              className={cn('icon-slot', styles.chevron, isExpanded && styles.chevronOpen)}
            />
          </button>
        )}

        {isExpanded && (
          <ul className={styles.list}>
            {group.pages.map((item, idx) =>
              isNavGroup(item)
                ? renderGroup(item, depth + 1, `${key}-${idx}`)
                : renderPageItem(item, depth)
            )}
          </ul>
        )}
      </div>
    );
  };

  return (
    <>
      {isOpen && <div onClick={onClose} className={styles.backdrop} />}

      <aside
        id="sidebar"
        className={cn(styles.aside, isOpen && styles.asideOpen, isResizing && styles.asideResizing)}
        style={{ '--sidebar-width': `${width}px` } as React.CSSProperties}
      >
        <div className={styles.mobileHeader}>
          <span>Navigation</span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close navigation"
            iconPrimary={<FontAwesomeIcon icon={faXmark} />}
          />
        </div>

        <nav aria-label="Sidebar Navigation" className={styles.nav}>
          {groups.map((group, idx) => renderGroup(group, 0, `group-${idx}`))}
        </nav>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize navigation"
          aria-controls="sidebar"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          className={styles.resizeHandle}
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerEnd}
          onPointerCancel={onResizePointerEnd}
          onKeyDown={onResizeKeyDown}
          onDoubleClick={() => commitWidth(DEFAULT_WIDTH)}
        />
      </aside>
    </>
  );
};

export default Sidebar;
